import React, { useState, useEffect } from "react";
import { useStateContext } from "contexts/ContextProvider";
import {
  Box,
  useTheme,
  Typography,
  Divider,
  useMediaQuery,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import { useParams } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { formatDate } from "helpers/ServiceHelpers";
import Dialog from "components/Dialog";
import api from "services/api";
import { OVERLAY_NAMES } from "constants/Overlays";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { usePageTitle } from "hooks/usePageTitle";
import { useAuth } from "contexts/AuthContext";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";

const BugReportProfile = () => {
  const [pageTitle, setPageTitle] = useState("Bug Report");
  usePageTitle(pageTitle);


  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(true);

  const { refreshPage, triggerPageRefresh } = useStateContext();
  const { setOverlayName, setOverlayData } = useStateContext();

  const [bugReportData, setBugReportData] = useState(null);

  const theme = useTheme();

  const navigate = useNavigate();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { openDialog, handleErrorDialog, closeDialog } = useStateContext();

  const { currentUser } = useAuth();

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);
  const [resolutionNotes, setResolutionNotes] = useState("");

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await api.get(`/bugReports/${id}`);
        if (response && response.data) {
          setBugReportData(response.data);
          setPageTitle(`Bug Report: ${response.data.Title || ""}`);
        }
      } catch (error) {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [handleErrorDialog, id, refreshPage, theme]);

  const deleteBugReport = async () => {
    try {
      await api.post("/bugReports/deleteBugReport", {
        bugReportId: id,
      });
      triggerPageRefresh("Bug Report Deleted Successfully!", theme);
      navigate("/bugReports");
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Unable to delete bug report. Please try again later.";
      handleErrorDialog(message, theme);
    }
  };

  const markAsFixed = async () => {
    try {
      await api.post("/bugReports/resolveBugReport", {
        bugReportId: id,
        resolvedBy: currentUser.id,
        resolutionNotes: resolutionNotes,
      });
      triggerPageRefresh("Bug Report Marked as Fixed Successfully!", theme);
      setResolutionNotes("");
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Unable to mark bug report as fixed. Please try again later.";
      handleErrorDialog(message, theme);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "New":
        return theme.palette.grey.main;
      case "In Progress":
        return theme.palette.warning.main;
      case "Resolved":
        return theme.palette.success.main;
      case "Closed":
        return theme.palette.primary.secondary;
      case "Won't Fix":
        return theme.palette.error.main;
      default:
        return theme.palette.grey.main;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "Critical":
        return theme.palette.error.main;
      case "High":
        return theme.palette.warning.main;
      case "Medium":
        return theme.palette.info.main;
      case "Low":
        return theme.palette.success.main;
      default:
        return theme.palette.grey.main;
    }
  };

  const BugReportCard = ({ children }) => (
    <Box
      sx={{
        bgcolor: theme.palette.background.default,
        borderRadius: "12px",
        border: `1px solid ${theme.palette.primary.border2}`,
        p: isSmallScreen ? "0.75rem" : "1.25rem",
        mx: "0.5rem",
        mt: "0.25rem",
        boxShadow: theme.shadows[3],
      }}
    >
      {children}
    </Box>
  );

  if (isLoading || !bugReportData) {
    return (
      <Box
        p="1rem"
        bgcolor={theme.palette.background.alt}
        sx={{
          flex: 1,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flexShrink: 0 }}></Box>
      </Box>
    );
  }

  const basicInformation = [
    {
      Title: "Status",
      Value: (
        <Chip
          label={bugReportData.Status}
          sx={{
            backgroundColor: getStatusColor(bugReportData.Status),
            color: "white",
            fontWeight: "bold",
          }}
        />
      ),
    },
    {
      Title: "Priority",
      Value: (
        <Chip
          label={bugReportData.Priority}
          sx={{
            backgroundColor: getPriorityColor(bugReportData.Priority),
            color: "white",
            fontWeight: "bold",
          }}
        />
      ),
    },
    {
      Title: "Category",
      Value: bugReportData.Category,
    },
    {
      Title: "Reported By",
      Value: bugReportData.Reporter?.Name || "N/A",
    },
    {
      Title: "Assigned To",
      Value: bugReportData.Assignee?.Name || "Unassigned",
    },
    {
      Title: "Reported Date",
      Value: formatDate(bugReportData.createdAt),
    },
    {
      Title: "Page URL",
      Value: bugReportData.PageURL ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            component="a"
            href={bugReportData.PageURL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: theme.palette.primary.main,
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
              wordBreak: "break-all",
            }}
          >
            {bugReportData.PageURL}
          </Typography>
          <OpenInNewRoundedIcon
            sx={{ fontSize: 16, color: theme.palette.primary.main }}
          />
        </Box>
      ) : (
        "N/A"
      ),
    },
    {
      Title: "Page Title",
      Value: bugReportData.PageTitle || "N/A",
    },
  ];

  const bugDetails = [
    {
      Title: "Description",
      Value: bugReportData.Description || "N/A",
    },
    {
      Title: "User Action",
      Value: bugReportData.UserAction || "N/A",
    },
    {
      Title: "Expected Behavior",
      Value: bugReportData.ExpectedBehavior || "N/A",
    },
    {
      Title: "Actual Behavior",
      Value: bugReportData.ActualBehavior || "N/A",
    },
    {
      Title: "Steps to Reproduce",
      Value: bugReportData.StepsToReproduce || "N/A",
    },
  ];

  const systemInformation = [
    {
      Title: "Browser",
      Value: bugReportData.BrowserInfo || "N/A",
    },
    {
      Title: "Operating System",
      Value: bugReportData.OSInfo || "N/A",
    },
    {
      Title: "Screen Resolution",
      Value: bugReportData.ScreenResolution || "N/A",
    },
    {
      Title: "User Agent",
      Value: bugReportData.UserAgent || "N/A",
    },
  ];

  return (
    <Box
      p="1rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Dialog />

      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid size={{ xs: 12 }} container justifyContent="flex-end">
            <IconButton
              onClick={handleMenuOpen}
              sx={{
                padding: "8px",
                backgroundColor: theme.palette.background.default,
                borderRadius: "12px",
                boxShadow:
                  "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                "&:hover": {
                  backgroundColor: theme.palette.primary.main,
                  "& .MuiSvgIcon-root": {
                    color: theme.palette.background.default,
                  },
                },
              }}
            >
              <MoreVertRoundedIcon
                sx={{
                  fontSize: 20,
                  color: theme.palette.primary.main,
                }}
              />
            </IconButton>
            <Menu
              open={menuOpen}
              onClose={handleMenuClose}
              anchorEl={menuAnchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              slotProps={{
                paper: {
                  sx: {
                    bgcolor: theme.palette.background.alt,
                    border: `1px solid ${theme.palette.divider}`,
                    "& .MuiListItemIcon-root": {
                      color: theme.palette.primary.main,
                    },
                  },
                },
              }}
            >
              {bugReportData.Status !== "Resolved" &&
                bugReportData.Status !== "Closed" && (
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      openDialog({
                        theme: theme,
                        title: "Mark as Fixed",
                        message: `Are you sure you want to mark "${bugReportData.Title}" as fixed?`,
                        customContent: (
                          <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Resolution Notes"
                            placeholder="Describe how the bug was resolved..."
                            value={resolutionNotes}
                            onChange={(e) => setResolutionNotes(e.target.value)}
                            sx={{ mt: 2 }}
                          />
                        ),
                        tertiaryAction: null,
                        secondaryAction: "Cancel",
                        primaryAction: "Mark as Fixed",
                        tertiaryOnClick: null,
                        secondaryOnClick: () => {
                          setResolutionNotes("");
                          closeDialog();
                        },
                        primaryOnClick: () => {
                          markAsFixed();
                          closeDialog();
                        },
                      });
                    }}
                  >
                    <ListItemIcon>
                      <CheckCircleOutlineRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="inherit">Resolve</Typography>
                  </MenuItem>
                )}
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  setOverlayData({
                    BugReportID: id,
                    BugReport: bugReportData,
                    Action: OVERLAY_NAMES.BUG_REPORT_ACTION_EDIT,
                  });
                  setOverlayName(OVERLAY_NAMES.BUG_REPORT_NEW);
                }}
              >
                <ListItemIcon>
                  <EditRoundedIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="inherit">Edit</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  openDialog({
                    theme: theme,
                    title: "Delete Confirmation",
                    message: `Are you sure you'd like to delete this bug report: "${bugReportData.Title}"?`,
                    customContent: null,
                    tertiaryAction: null,
                    secondaryAction: "Cancel",
                    primaryAction: "Delete",
                    tertiaryOnClick: null,
                    secondaryOnClick: () => {
                      closeDialog();
                    },
                    primaryOnClick: () => {
                      deleteBugReport();
                      closeDialog();
                    },
                  });
                }}
              >
                <ListItemIcon>
                  <DeleteRoundedIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="inherit">Delete</Typography>
              </MenuItem>
            </Menu>
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          my: "1rem",
          mx: isSmallScreen ? "0.5rem" : "0.75rem",
          pb: 3,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Basic Information Card */}
        <BugReportCard>
          <Typography
            variant={isSmallScreen ? "h4" : "h3"}
            fontWeight="bold"
            gutterBottom
          >
            Basic Information
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            {basicInformation.map((item, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`basic-${index}`}>
                <Typography variant="caption" color="text.secondary">
                  {item.Title}
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>
                  {item.Value}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </BugReportCard>

        {/* Bug Details Card */}
        <BugReportCard>
          <Typography
            variant={isSmallScreen ? "h4" : "h3"}
            fontWeight="bold"
            gutterBottom
          >
            Bug Details
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            {bugDetails.map((item, index) => (
              <Grid size={{ xs: 12 }} key={`details-${index}`}>
                <Typography variant="caption" color="text.secondary">
                  {item.Title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                >
                  {item.Value}
                </Typography>
                {index < bugDetails.length - 1 && <Divider sx={{ my: 1.5 }} />}
              </Grid>
            ))}
          </Grid>
        </BugReportCard>

        {/* System Information Card */}
        <BugReportCard>
          <Typography
            variant={isSmallScreen ? "h4" : "h3"}
            fontWeight="bold"
            gutterBottom
          >
            System Information
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            {systemInformation.map((item, index) => (
              <Grid size={{ xs: 12, sm: 6 }} key={`system-${index}`}>
                <Typography variant="caption" color="text.secondary">
                  {item.Title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mt: 0.5,
                    wordBreak: "break-all",
                  }}
                >
                  {item.Value}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </BugReportCard>

        {/* Related Items Card */}
        {(bugReportData.RelatedService ||
          bugReportData.RelatedClient ||
          bugReportData.RelatedLinguist) && (
          <BugReportCard>
            <Typography
              variant={isSmallScreen ? "h4" : "h3"}
              fontWeight="bold"
              gutterBottom
            >
              Related Items
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              {bugReportData.RelatedService && (
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Related Service
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {bugReportData.RelatedService.ProjectNumber ||
                      bugReportData.RelatedService.Name}
                  </Typography>
                </Grid>
              )}

              {bugReportData.RelatedClient && (
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Related Client
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {bugReportData.RelatedClient.Account}
                  </Typography>
                </Grid>
              )}

              {bugReportData.RelatedLinguist && (
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Related Linguist
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {bugReportData.RelatedLinguist.Name}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </BugReportCard>
        )}

        {/* Resolution Card */}
        {bugReportData.ResolutionNotes && (
          <BugReportCard>
            <Typography
              variant={isSmallScreen ? "h4" : "h3"}
              fontWeight="bold"
              gutterBottom
            >
              Resolution
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">
                  Resolution Details
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                >
                  {bugReportData.ResolutionNotes}
                </Typography>
              </Grid>

              {bugReportData.Resolver && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1.5 }} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">
                      Resolved By
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {bugReportData.Resolver.Name}
                    </Typography>
                  </Grid>
                </>
              )}

              {bugReportData.ResolvedAt && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Resolved Date
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {formatDate(bugReportData.ResolvedAt)}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </BugReportCard>
        )}
      </Box>
    </Box>
  );
};

export default BugReportProfile;
